import { Button, makeStyles, Paper, Theme, Typography } from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import Popper from "@material-ui/core/Popper";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PARTNER_PORTAL_TOUR_TARGETS } from "../../shared/PartnerPortalTourTargets";

interface TourStep
{
  id: string;
  targetSelector: string;
  messageKey: string;
  isInRun?: () => boolean;
  isEligible?: () => boolean;
}

interface ActiveTourStep
{
  step: TourStep;
  displayIndex: number;
  totalCount: number;
  anchor: HTMLElement;
}

interface FeatureTipsTourProps
{
  restartSignal?: number;
}

const START_DELAY_AFTER_PREREQUISITES_MS = 5000;
const POLL_INTERVAL_MS = 500;
const DISMISSED_STORAGE_PREFIX = "partnerPortal.featureTour.dismissed.";
const INSTALL_STEP_SELECTOR = `[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.installMenuItem}"]`;
const HELP_STEP_SELECTOR = `[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.helpMenuItem}"]`;
const NOTIFICATIONS_STEP_SELECTOR = `[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.notificationsButton}"]`;

const useStyles = makeStyles((theme: Theme) => ({
  popper: {
    zIndex: 1400,
  },
  bubble: {
    position: "relative",
    width: "calc(100vw - 24px)",
    maxWidth: 360,
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.36)",
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
    boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
    '&::before': {
      content: '""',
      position: "absolute",
      top: -8,
      left: "var(--tour-arrow-left, 26px)",
      width: 14,
      height: 14,
      transform: "rotate(45deg)",
      backgroundColor: green[600],
      borderTop: "1px solid rgba(255,255,255,0.36)",
      borderLeft: "1px solid rgba(255,255,255,0.36)",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  counter: {
    color: "rgba(255,255,255,0.92)",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(1.5),
    gap: theme.spacing(1),
  },
  actionButton: {
    flex: 1,
    minHeight: 42,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
      marginLeft: -2,
    },
  },
  dismissButton: {
    backgroundColor: "rgba(255,255,255,0.16)",
    color: "#ffffff",
    '&:hover': {
      backgroundColor: "rgba(255,255,255,0.24)",
    },
  },
  doneButton: {
    backgroundColor: "#ffffff",
    color: green[700],
    '&:hover': {
      backgroundColor: "#eaf6ea",
    },
  },
  message: {
    fontSize: 15,
    lineHeight: 1.4,
  },
}));

const isElementVisible = (element: HTMLElement): boolean =>
{
  if(!element.isConnected) return false;

  const style = window.getComputedStyle(element);
  if(style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;

  const rect = element.getBoundingClientRect();
  if(rect.width <= 0 || rect.height <= 0) return false;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return rect.bottom > 0
    && rect.right > 0
    && rect.left < viewportWidth
    && rect.top < viewportHeight;
};

const isDismissedPermanently = (stepId: string): boolean =>
{
  try
  {
    return localStorage.getItem(`${DISMISSED_STORAGE_PREFIX}${stepId}`) === "1";
  }
  catch(_error)
  {
    return false;
  }
};

const dismissPermanently = (stepId: string): void =>
{
  try
  {
    localStorage.setItem(`${DISMISSED_STORAGE_PREFIX}${stepId}`, "1");
  }
  catch(_error)
  {
  }
};

const clearDismissedPermanently = (): void =>
{
  try
  {
    Object.keys(localStorage)
      .filter(key => key.startsWith(DISMISSED_STORAGE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  }
  catch(_error)
  {
  }
};

const isAssistantStateResolved = (): boolean =>
{
  if(typeof document === "undefined") return false;

  const stateElement = document.querySelector("[data-tour-assistant-ready]");
  if(!(stateElement instanceof HTMLElement)) return false;

  return stateElement.getAttribute("data-tour-assistant-ready") === "1";
};

const isAnyTargetVisible = (selectors: string[]): boolean =>
{
  return selectors.some(selector =>
  {
    const element = document.querySelector(selector);
    return element instanceof HTMLElement && isElementVisible(element);
  });
};

const isPWAInstalled = (): boolean =>
{
  if(typeof window === "undefined") return false;
  if(window.matchMedia?.("(display-mode: standalone)").matches) return true;
  return Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
};

const canShowInstallTip = (): boolean =>
{
  if(typeof window === "undefined") return false;
  return !isPWAInstalled()
    && typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && Boolean((window as any).deferredPWAInstallPrompt);
};

const canUseNotifications = (): boolean =>
{
  if(typeof window === "undefined") return false;
  if(typeof navigator === "undefined") return false;
  return "serviceWorker" in navigator
    && "PushManager" in window
    && typeof Notification !== "undefined";
};

const isNotificationsEnabled = (): boolean =>
{
  const element = document.querySelector(NOTIFICATIONS_STEP_SELECTOR);
  if(element instanceof HTMLElement)
  {
    const enabled = element.getAttribute("data-tour-notifications-enabled");
    if(enabled === "1") return true;
    if(enabled === "0") return false;
  }

  return typeof Notification !== "undefined" && Notification.permission === "granted";
};

export default function FeatureTipsTour({ restartSignal }: FeatureTipsTourProps)
{
  const { t } = useTranslation();
  const classes = useStyles();
  const [isStarted, setIsStarted] = useState(false);
  const [isAssistantReady, setIsAssistantReady] = useState<boolean>(() => isAssistantStateResolved());
  const [isRunStopped, setIsRunStopped] = useState(false);
  const [runStepIds, setRunStepIds] = useState<string[]>([]);
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
  const [activeStep, setActiveStep] = useState<ActiveTourStep | null>(null);
  const [arrowLeft, setArrowLeft] = useState(26);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const previousRestartSignalRef = useRef<number | undefined>(restartSignal);

  // Add or edit tour steps here by selector + i18n key.
  const tourSteps = useMemo<TourStep[]>(() => ([
    {
      id: "profileMenu",
      targetSelector: `[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.profileMenuButton}"]`,
      messageKey: "partnerPortal.featureTour.profileMenu",
      isInRun: () => document.querySelector(`[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.profileMenuButton}"]`) instanceof HTMLElement
        && !isAnyTargetVisible([INSTALL_STEP_SELECTOR, HELP_STEP_SELECTOR]),
      isEligible: () => !isAnyTargetVisible([INSTALL_STEP_SELECTOR, HELP_STEP_SELECTOR]),
    },
    {
      id: "install",
      targetSelector: INSTALL_STEP_SELECTOR,
      messageKey: "partnerPortal.featureTour.install",
      isInRun: () => canShowInstallTip(),
    },
    {
      id: "notifications",
      targetSelector: NOTIFICATIONS_STEP_SELECTOR,
      messageKey: "partnerPortal.featureTour.notifications",
      isInRun: () => canUseNotifications() && !isNotificationsEnabled(),
    },
    {
      id: "help",
      targetSelector: HELP_STEP_SELECTOR,
      messageKey: "partnerPortal.featureTour.help",
    },
    {
      id: "searchMode",
      targetSelector: `[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.searchModeButton}"]`,
      messageKey: "partnerPortal.featureTour.searchMode",
    },
    {
      id: "machineAssistant",
      targetSelector: `[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.assistantButton}"]`,
      messageKey: "partnerPortal.featureTour.machineAssistant",
      isInRun: () => document.querySelector(`[data-tour-id="${PARTNER_PORTAL_TOUR_TARGETS.assistantButton}"]`) instanceof HTMLElement,
    },
  ]), []);

  useEffect(() =>
  {
    if(isAssistantReady) return;

    const intervalId = window.setInterval(() =>
    {
      if(isAssistantStateResolved())
      {
        setIsAssistantReady(true);
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isAssistantReady]);

  useEffect(() =>
  {
    if(!isAssistantReady) return;

    const timeoutId = window.setTimeout(() => setIsStarted(true), START_DELAY_AFTER_PREREQUISITES_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isAssistantReady]);

  useEffect(() =>
  {
    if(previousRestartSignalRef.current === restartSignal) return;

    previousRestartSignalRef.current = restartSignal;
    clearDismissedPermanently();
    setIsStarted(isAssistantReady);
    setIsRunStopped(false);
    setRunStepIds([]);
    setCompletedStepIds([]);
    setActiveStep(null);
    setArrowLeft(26);
  }, [restartSignal, isAssistantReady]);

  useEffect(() =>
  {
    if(!isStarted || isRunStopped || runStepIds.length > 0) return;

    const stepIdsInRun = tourSteps
      .filter(step => !isDismissedPermanently(step.id))
      .filter(step => !step.isInRun || step.isInRun())
      .map(step => step.id);

    setRunStepIds(stepIdsInRun);
    if(stepIdsInRun.length === 0)
    {
      setIsRunStopped(true);
    }
  }, [isStarted, isRunStopped, runStepIds.length, tourSteps]);

  useEffect(() =>
  {
    if(!isStarted || isRunStopped || runStepIds.length === 0) return;

    const updateActiveStep = () =>
    {
      let nextActiveStep: ActiveTourStep | null = null;

      const stepsInRun = runStepIds
        .map(stepId => tourSteps.find(step => step.id === stepId))
        .filter((step): step is TourStep => Boolean(step));

      const completedCount = stepsInRun.filter(step => completedStepIds.includes(step.id)).length;

      for(let index = 0; index < stepsInRun.length; index++)
      {
        const step = stepsInRun[index];
        if(completedStepIds.includes(step.id)) continue;
        if(step.isEligible && !step.isEligible()) continue;

        const element = document.querySelector(step.targetSelector);
        if(!(element instanceof HTMLElement) || !isElementVisible(element)) continue;

        nextActiveStep = {
          step,
          displayIndex: completedCount + 1,
          totalCount: stepsInRun.length,
          anchor: element,
        };
        break;
      }

      setActiveStep(previous =>
      {
        if(!nextActiveStep && !previous) return previous;
        if(!nextActiveStep && previous) return null;

        if(
          nextActiveStep
          && previous
          && previous.step.id === nextActiveStep.step.id
          && previous.anchor === nextActiveStep.anchor
        )
        {
          return previous;
        }

        return nextActiveStep;
      });
    };

    updateActiveStep();
    const intervalId = window.setInterval(updateActiveStep, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isStarted, isRunStopped, runStepIds, completedStepIds, tourSteps]);

  useEffect(() =>
  {
    if(!activeStep) return;

    const updateArrowPosition = () =>
    {
      if(!activeStep || !bubbleRef.current) return;

      const bubbleRect = bubbleRef.current.getBoundingClientRect();
      const anchorRect = activeStep.anchor.getBoundingClientRect();

      const centerX = anchorRect.left + (anchorRect.width / 2);
      const rawLeft = centerX - bubbleRect.left - 7;
      const minLeft = 8;
      const maxLeft = Math.max(bubbleRect.width - 22, minLeft);
      const nextLeft = Math.max(minLeft, Math.min(maxLeft, rawLeft));
      setArrowLeft(nextLeft);
    };

    const mountIntervalId = window.setInterval(updateArrowPosition, 120);
    const rafId = window.requestAnimationFrame(updateArrowPosition);
    window.addEventListener("resize", updateArrowPosition);
    window.addEventListener("scroll", updateArrowPosition, true);

    return () =>
    {
      window.clearInterval(mountIntervalId);
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateArrowPosition);
      window.removeEventListener("scroll", updateArrowPosition, true);
    };
  }, [activeStep]);

  const stopRunForNow = () =>
  {
    setIsRunStopped(true);
    setActiveStep(null);
  };

  const dismissStepAndContinue = () =>
  {
    if(!activeStep) return;

    dismissPermanently(activeStep.step.id);
    setCompletedStepIds(existing => existing.includes(activeStep.step.id)
      ? existing
      : [...existing, activeStep.step.id]);
    setActiveStep(null);
  };

  if(!activeStep || isRunStopped) return null;

  const bubbleStyle = { "--tour-arrow-left": `${arrowLeft}px` } as React.CSSProperties;

  return (
    <Popper
      open
      anchorEl={activeStep.anchor}
      placement="bottom-start"
      className={classes.popper}
    >
      <Paper ref={bubbleRef} className={classes.bubble} style={bubbleStyle} data-tour-bubble="partner-portal-feature-tip">
        <div className={classes.header}>
          <Typography className={classes.counter} data-tour-counter>{`${activeStep.displayIndex}/${activeStep.totalCount}`}</Typography>
        </div>
        <Typography className={classes.message}>{t(activeStep.step.messageKey)}</Typography>
        <div className={classes.actions}>
          <Button
            size="small"
            variant="contained"
            className={`${classes.actionButton} ${classes.dismissButton}`}
            onClick={stopRunForNow}
            aria-label={t("partnerPortal.featureTour.dismiss")}
            title={t("partnerPortal.featureTour.dismiss")}
            startIcon={<CloseIcon fontSize="small" />}
          >
            {t("partnerPortal.featureTour.dismiss")}
          </Button>
          <Button
            size="small"
            variant="contained"
            className={`${classes.actionButton} ${classes.doneButton}`}
            onClick={dismissStepAndContinue}
            aria-label={t("partnerPortal.featureTour.done")}
            title={t("partnerPortal.featureTour.done")}
            startIcon={<CheckIcon fontSize="small" />}
          >
            {t("partnerPortal.featureTour.done")}
          </Button>
        </div>
      </Paper>
    </Popper>
  );
}
